import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/components/Toast';
import { DeveloperPicker } from '@/components/DeveloperPicker';
import { getAvailability, upsertAvailability } from '@/services/alerts';

// datetime-local wants "YYYY-MM-DDTHH:mm" in local time.
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export function AvailabilityPage() {
  const { developer } = useAuth();
  const toast = useToast();
  const [isOoo, setIsOoo] = useState(false);
  const [until, setUntil] = useState('');
  const [delegate, setDelegate] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!developer) return;
    getAvailability(developer.id).then((a) => {
      if (!a) return;
      setIsOoo(a.is_ooo);
      setUntil(toLocalInput(a.ooo_until));
      setDelegate(a.delegate_developer_id);
      setNote(a.note ?? '');
    }).catch((e) => toast(e.message, 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [developer?.id]);

  async function save() {
    if (!developer) return;
    setSaving(true);
    try {
      await upsertAvailability({
        developer_id: developer.id,
        is_ooo: isOoo,
        ooo_since: isOoo ? new Date().toISOString() : null,
        ooo_until: isOoo && until ? new Date(until).toISOString() : null,
        delegate_developer_id: delegate,
        note: note || null,
      });
      toast('Availability saved', 'success');
    } catch (e: any) { toast(e.message ?? 'Failed to save', 'error'); }
    finally { setSaving(false); }
  }

  return (
    <div className="page">
      <h1>Availability</h1>
      <p className="muted">When you're out of office, your alerts (including assignments) route to your delegate instead.</p>

      <div className="card">
        <label className="checkline big">
          <input type="checkbox" checked={isOoo} onChange={(e) => setIsOoo(e.target.checked)} />
          I'm out of office
        </label>

        <div className={isOoo ? '' : 'disabled-block'}>
          <label>Until (optional — blank = indefinite)</label>
          <input className="input" type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} disabled={!isOoo} />

          <label>Delegate</label>
          <DeveloperPicker value={delegate} onChange={setDelegate} excludeId={developer?.id} placeholder="No delegate — alerts fall back to everyone" />

          <label>Note (optional)</label>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. on holiday, back Monday" />
        </div>

        <button className="btn btn-primary" disabled={saving} onClick={() => void save()}>Save</button>
      </div>
    </div>
  );
}
